/**
 * Phase 140B.1 — Audio Enhancement Settings hook.
 *
 * Single source of truth for the broadcaster's WebRTC audio capture
 * preferences. Persists to localStorage so the broadcaster's previous
 * choices stick across sessions. Defaults are ALL-ON because that's the
 * single most impactful win for outdoor sports broadcasts (wind, crowd,
 * traffic noise — kills audibility without these constraints applied).
 *
 * Output shape:
 *   {
 *     settings: { noiseSuppression, echoCancellation, autoGainControl },
 *     setSetting: (key, value) => void,
 *     audioConstraints: object — directly usable as LiveKitRoom's `audio` prop.
 *   }
 *
 * The `audioConstraints` object explicitly omits any key that's false so
 * downstream WebRTC code falls back to browser defaults rather than
 * forcing the constraint off (some Android Chrome versions interpret
 * `noiseSuppression: false` as "user explicitly disabled" and refuse to
 * apply any noise-suppression algorithm even if hardware supports it).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'livex_audio_enhancement_v1';

const DEFAULTS = {
  noiseSuppression: true,
  echoCancellation: true,
  autoGainControl: true,
};

function _loadFromStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return { ...DEFAULTS };
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return {
      noiseSuppression: parsed.noiseSuppression !== false,
      echoCancellation: parsed.echoCancellation !== false,
      autoGainControl: parsed.autoGainControl !== false,
    };
  } catch (_e) {
    return { ...DEFAULTS };
  }
}

export default function useAudioEnhancementSettings() {
  const [settings, setSettings] = useState(_loadFromStorage);

  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (_e) { /* localStorage unavailable — settings still hold in-memory */ }
  }, [settings]);

  const setSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: !!value }));
  }, []);

  // Build the LiveKitRoom-compatible `audio` prop. LiveKit accepts
  // `boolean | AudioCaptureOptions` where AudioCaptureOptions extends
  // MediaTrackConstraints. We pass only the enabled flags as `true` and
  // leave disabled flags absent so the browser uses its own default for
  // that constraint (less aggressive than forcing `false`).
  const audioConstraints = useMemo(() => {
    const out = {};
    if (settings.noiseSuppression) out.noiseSuppression = true;
    if (settings.echoCancellation) out.echoCancellation = true;
    if (settings.autoGainControl) out.autoGainControl = true;
    // If everything is disabled, fall back to a bare-`true` (default mic
    // capture). LiveKit treats `true` and `{}` differently — `{}` skips
    // every constraint, `true` lets LiveKit pick sensible defaults.
    if (Object.keys(out).length === 0) return true;
    return out;
  }, [settings]);

  return { settings, setSetting, audioConstraints };
}
